import { JSX, useState } from 'react';
import { isObject } from 'underscore';

import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import UploadLarge from '@ui/uploads/upload-large';
import {
  GetUploadEndpoint,
  getUploadFilename,
  UploadFileToProvider,
  SetItemContentType,
} from '@ui/uploads/validations';
import { InitialAspectRatioOptionsEnum } from '@/types/images.types';

type ImageUploadProps = {
  onUpload: (url: string) => Promise<void>;
  img?: string | null;
  style?: React.CSSProperties;
  CustomTitle?: JSX.Element | string;
  maxSizeInBytes?: number;
  title?: string;
  subtitle?: string;
  description?: string;
  wrapperClassNames?: string;
  hideIconIfImage?: boolean;
  disableEditingAspectRatio?: boolean;
  initialAspectRatio?: InitialAspectRatioOptionsEnum;
  standard?: boolean;
  editable?: boolean;
  placeholder?: string;
  onDelete?: () => void;
  hideText?: boolean;
  hidePlaceholderImage?: boolean;
};

const ImageUpload = ({
  title,
  subtitle,
  description,
  onUpload,
  img,
  style,
  CustomTitle,
  maxSizeInBytes,
  wrapperClassNames,
  hideIconIfImage,
  disableEditingAspectRatio,
  initialAspectRatio,
  standard,
  editable,
  placeholder,
  onDelete,
  hideText,
  hidePlaceholderImage,
}: ImageUploadProps) => {
  const [uploading, setUpLoading] = useState(false);

  const handleFile = async (file: File | FileList) => {
    try {
      if (file instanceof File) {
        setUpLoading(true);

        const endpoint = await GetUploadEndpoint({
          filename: getUploadFilename(file.name),
        });

        if (isObject(endpoint) && endpoint.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );

          SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);

          if (finalUrl) {
            await onUpload(finalUrl);
          }
        }
      }
    } catch (error) {
      console.error(error);
      handleImageUploadErrors(error);
    } finally {
      setUpLoading(false);
    }
  };

  return (
    <div className="overflow-hidden width-auto height-100-percent">
      <div style={{ position: 'relative', height: '100%', width: '100%' }}>
        <div
          style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
          className="flex-column jc-center ai-center width-100-percent height-100-percent"
        >
          <UploadLarge
            wrapperClassNames={wrapperClassNames}
            displayImageUrl={img}
            uploading={uploading}
            maxSizeInBytes={maxSizeInBytes}
            style={{ ...(style || {}) }}
            CustomTitle={CustomTitle}
            title={title}
            subtitle={subtitle}
            description={description}
            onFile={handleFile}
            hideIconIfImage={hideIconIfImage}
            allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
            disableEditingAspectRatio={disableEditingAspectRatio}
            initialAspectRatio={initialAspectRatio}
            standard={standard}
            editable={editable}
            placeholder={placeholder}
            onDelete={onDelete}
            hideText={hideText}
            hidePlaceholderImage={hidePlaceholderImage}
          />
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
